import React, { useEffect, useRef, useState } from 'react';
import { useExemplos, EXEMPLOS_GENERICOS } from '../utils/exemplosPessoais';
import { MODO_DEMO } from '../demo/modo';
import { EVENTO_ABRIR_CATEGORIAS } from './GerenciadorDeCategorias';
import {
  askAdvisor, AdvisorAnswer, aceitarCoach, PropostaDeCoach, confirmarAviso, PropostaDeAviso,
  lerMarcadorDoFinn, marcarMarcadorVisto, MarcadorDoFinn, PortaDoFinn,
} from '../services/api';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import AskField from './AskField';
import Resposta from './AdvisorAnswer';
import './AdvisorChat.css';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import FinnAvatar from './FinnAvatar';
import QuemEhOFinn from './QuemEhOFinn';
import PerguntaDoFinn from './PerguntaDoFinn';

/**
 * O chat de análise — perguntar, e depois aprofundar.
 *
 * ── Por que conversa aqui, e não no lançamento ───────────────────────────
 *
 * O design doc do bot de WhatsApp descartou chat com um argumento correto:
 * "chat pergunta um campo por vez". Mas aquilo era para ESCREVER dado, onde
 * cada pergunta é atrito. Para LER, conversa é a forma natural — "e se fosse
 * 2000?", "e em julho?" só existem porque há um fio anterior.
 *
 * ── A garantia que a memória NÃO pode quebrar ────────────────────────────
 *
 * Nenhum número deste app passa por um modelo de linguagem. Com conversa isso
 * fica frágil: bastaria mandar a resposta anterior de volta e o modelo passaria
 * a raciocinar sobre valores que VIU em vez de valores CALCULADOS.
 *
 * Por isso o histórico enviado carrega só a PERGUNTA e a INTENÇÃO resolvida —
 * nunca o resultado. E a filtragem de verdade acontece no servidor
 * (`sanitizeAdvisorHistory`), não aqui: uma garantia que depende do frontend
 * se comportar não é garantia.
 *
 * A memória vive só na sessão. Recarregar a página zera, e isso é o custo
 * aceito de não criar uma tabela guardando o que ela pergunta sobre o próprio
 * dinheiro.
 *
 * ── O marcador do Finn, e por que ele NÃO quebra aquela promessa ─────────
 *
 * Desde 31/08 a bolha também mostra o que o Finn tem a dizer sem ser
 * perguntado. Isso persiste — e é uma coisa diferente do que a promessa acima
 * protege:
 *
 *   persiste  →  o que o FINN disse, e se ela já viu
 *   evapora   →  o que ELA perguntou
 *
 * Um é conteúdo que o app produziu e precisa saber se foi entregue. O outro é
 * registro de comportamento. Dar iniciativa ao chat não é autorização para
 * começar a gravar as perguntas.
 */

interface Mensagem {
  de: 'voce' | 'app';
  texto?: string;
  resposta?: AdvisorAnswer;
}

/**
 * A ênfase `*assim*` das mensagens do Finn.
 *
 * ── Por que o painel entende isso ────────────────────────────────────────
 *
 * Não é o painel aprendendo WhatsApp. `*x*` é a convenção de ênfase DA
 * MENSAGEM, e `executarOferta` é a mesma função nos dois canais — ela devolve
 * uma frase só. O WhatsApp lê nativamente; aqui são cinco linhas.
 *
 * A alternativa seria a função devolver estrutura e cada canal formatar. Vale
 * o dia em que a frase precisar de mais que negrito; hoje seria refatorar
 * todas as mensagens para não escrever cinco linhas.
 */
const comEnfase = (texto: string): React.ReactNode =>
  texto.split(/(\*[^*\n]+\*)/g).map((p, i) =>
    p.startsWith('*') && p.endsWith('*') && p.length > 2
      ? <strong key={i}>{p.slice(1, -1)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>);

/**
 * O que o botão do marcador diz, por destino.
 *
 * O rótulo promete a AÇÃO, não o lugar: "criar o limite" e não "ir para
 * metas". Quem lê acabou de receber uma proposta, e o botão é a resposta a
 * ela — dizer o nome da tela faria a pessoa ter que traduzir sozinha.
 *
 * O fallback existe para um destino novo no servidor chegando antes de a tela
 * saber dele: melhor um "Ver" genérico que um botão sem texto.
 */
const ROTULO_DO_DESTINO: Record<string, string> = {
  'sec-reminders': 'Criar o lembrete',
  'sec-goals': 'Criar o limite',
  whatsapp: 'Ligar o WhatsApp',
  conector: 'Conectar a IA',
  categorias: 'Responder em Categorias',
  coach: 'Ver a meta',
};

/** Avisa o card de Metas de que nasceu uma meta fora dele. */
export const EVENTO_METAS_MUDARAM = 'fintracking:metas-mudaram';

/**
 * A proposta de Finn Coach, com as duas saídas.
 *
 * ── A única coisa que este painel cria ───────────────────────────────────
 *
 * O chat do site orienta e não cria — a regra dela continua valendo para todo
 * o resto. Esta é a exceção que ela abriu em 05/10: só aparece quando ELA
 * pediu orientação, mostra nome, valor e prazo antes de qualquer coisa
 * existir, e só grava depois do toque.
 *
 * "Agora não" tem o mesmo peso de "Pode me cobrar". Uma recusa escondida
 * transformaria a proposta em empurrão, que é o que a regra do painel proíbe.
 *
 * O botão manda só a categoria. O valor que a meta ganha é o que o servidor
 * recalcular, não o que esta tela mostrou.
 */
const PropostaDoCoach: React.FC<{ p: PropostaDeCoach }> = ({ p }) => {
  const [estado, setEstado] = useState<'aberta' | 'enviando' | 'aceita' | 'recusada'>('aberta');
  const [erro, setErro] = useState('');

  if (estado === 'recusada') return null;
  if (estado === 'aceita') {
    return (
      <div className="ac-coach">
        <p>Combinado. {p.categoria} está no Finn Coach, e eu começo a te cobrar no próximo placar.</p>
        <button className="ac-finn-ir" onClick={() => { window.location.hash = 'sec-goals'; }}>
          Ver a meta
        </button>
      </div>
    );
  }

  const aceitar = async () => {
    setEstado('enviando');
    setErro('');
    try {
      await aceitarCoach(p.categoryId);
      setEstado('aceita');
      window.dispatchEvent(new Event(EVENTO_METAS_MUDARAM));
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui criar a meta agora.'));
      setEstado('aberta');
    }
  };

  return (
    <div className="ac-coach">
      <span className="ac-coach-selo">Finn Coach</span>
      <p>{p.texto}</p>
      {erro && <p className="ac-coach-erro" role="alert">{erro}</p>}
      <div className="ac-coach-acoes">
        <button className="ac-coach-btn sim" onClick={aceitar} disabled={estado === 'enviando'} aria-busy={estado === 'enviando'}>
          Pode me cobrar
        </button>
        <button className="ac-coach-btn" onClick={() => setEstado('recusada')} disabled={estado === 'enviando'}>
          Agora não
        </button>
      </div>
    </div>
  );
};

/**
 * O botão do aviso que ela pediu ("me avisa se...", 27/10).
 *
 * A segunda escrita que nasce no chat, e pelo mesmo motivo da primeira: ELA
 * pediu. O texto da proposta já está na resposta, escrito pelo código; aqui
 * fica só a confirmação. O botão manda o token que o servidor assinou, nunca
 * o valor: quem decide o que grava é a proposta que o código montou.
 *
 * "Agora não" com o mesmo peso, como na proposta de coach.
 */
const PropostaDoAviso: React.FC<{ p: PropostaDeAviso }> = ({ p }) => {
  const [estado, setEstado] = useState<'aberta' | 'enviando' | 'feita' | 'recusada'>('aberta');
  const [feito, setFeito] = useState('');
  const [erro, setErro] = useState('');

  if (estado === 'recusada') return null;
  if (estado === 'feita') return <div className="ac-coach"><p>{feito}</p></div>;

  const confirmar = async () => {
    setEstado('enviando');
    setErro('');
    try {
      const r = await confirmarAviso(p.token);
      setFeito(r.resposta);
      setEstado('feita');
    } catch (e) {
      setErro(mensagemDeErro(e, p.acao === 'criar' ? 'Não consegui criar o aviso agora.' : 'Não consegui desligar o aviso agora.'));
      setEstado('aberta');
    }
  };

  return (
    <div className="ac-coach">
      {erro && <p className="ac-coach-erro" role="alert">{erro}</p>}
      <div className="ac-coach-acoes">
        <button className="ac-coach-btn sim" onClick={confirmar} disabled={estado === 'enviando'} aria-busy={estado === 'enviando'}>
          {p.botao}
        </button>
        <button className="ac-coach-btn" onClick={() => setEstado('recusada')} disabled={estado === 'enviando'}>
          Agora não
        </button>
      </div>
    </div>
  );
};

interface Props {
  refreshTrigger?: number;
  /**
   * Abre o modal do WhatsApp — ele mora no `AppContent`, não aqui.
   *
   * O marcador pode convidar a ligar o WhatsApp, e esse é o único destino que
   * não é uma seção do quadro: ele abre um modal pelo menu do avatar.
   */
  onAbrirWhatsapp?: () => void;
  /**
   * Abre a tela de conectar o Claude/ChatGPT — mesmo caso do WhatsApp: o
   * marcador pode convidar, e o destino é um modal do `AppContent`, não uma
   * seção do quadro.
   */
  onAbrirConector?: () => void;
  /**
   * A pergunta do Finn ("o que você quer que o seu dinheiro faça por você?").
   *
   * Era um modal que abria sozinho. Desde 08/10 mora aqui, como fala dele: o
   * ponto na bolha avisa, e ela responde quando abrir. `null` = não há o que
   * perguntar. Quem decide é o servidor, e quem guarda as portas é o
   * `AppContent`, que também recarrega as metas quando ela declara.
   */
  /**
   * Abre o chat com uma pergunta já escrita, sem enviar — o passo "pedir ao
   * Finn" do Comece por aqui. O `em` faz o mesmo pedido valer de novo.
   */
  abrirCom?: { texto: string; em: number } | null;
  pergunta?: {
    portas: PortaDoFinn[];
    nome?: string;
    onDeclarou: () => void;
    onDepois: () => void;
  } | null;
}

const AdvisorChat: React.FC<Props> = ({ refreshTrigger, onAbrirWhatsapp, onAbrirConector, pergunta = null, abrirCom = null }) => {
  const [aberto, setAberto] = useState(false);
  useEffect(() => { if (abrirCom) setAberto(true); }, [abrirCom]);
  /* Ela acabou de responder: o Finn confirma no mesmo balão, em vez de a pergunta sumir calada. */
  const [respondeu, setRespondeu] = useState(false);

  /*
   * O que o Finn tem a dizer. `null` = nada, e a bolha fica como sempre foi.
   *
   * Erro de rede vira silêncio: o marcador é um extra, e uma bolha que some
   * porque a rota falhou seria pior que uma bolha sem aviso.
   */
  const [marcador, setMarcador] = useState<MarcadorDoFinn | null>(null);
  /* A apresentação dele, quando ela toca no avatar. */
  const [quemEh, setQuemEh] = useState(false);


  useEffect(() => {
    let vivo = true;
    lerMarcadorDoFinn()
      .then(m => { if (vivo) setMarcador(m); })
      .catch(() => {/* silêncio de propósito — ver a nota acima */});
    return () => { vivo = false; };
  }, [refreshTrigger]);

  /* No celular o chat é tela cheia: voltar fecha ele antes de sair do app. */
  useFechavelComVoltar(aberto, React.useCallback(() => setAberto(false), []));
  const [msgs, setMsgs] = useState<Mensagem[]>([]);
  /*
   * Os exemplos com as categorias dela. Continuam sendo PERGUNTAS que ela
   * pode fazer, e nunca uma afirmação sobre o dinheiro dela: "quanto gastei
   * em Pet?" cabe aqui; "você gastou muito em Pet" seria insight não pedido.
   */
  const pessoais = useExemplos().chat;
  /*
   * Na demonstração os três chips são os de sempre: lá só essas três
   * perguntas têm resposta de exemplo escrita (`demo/dados.ts`), e um chip
   * "quanto gastei em Transporte?" seria respondido falando de mercado.
   */
  const exemplosDoChat = MODO_DEMO ? EXEMPLOS_GENERICOS.chat : pessoais;
  const fimRef = useRef<HTMLDivElement>(null);

  /*
   * O painel acompanha o TECLADO do celular (05/10).
   *
   * No celular o painel é tela cheia, preso às quatro bordas — mas as bordas
   * de `position: fixed` são as da página, e o teclado não encolhe a página:
   * ele sobe POR CIMA. O campo de pergunta mora no rodapé do painel, então
   * ficava atrás do teclado, e ela digitava sem ver o que estava escrevendo.
   *
   * `visualViewport` é a parte da tela que de fato está à mostra, e muda
   * quando o teclado abre. O painel passa a ter exatamente essa altura e esse
   * topo; o rodapé encosta no teclado em vez de sumir sob ele.
   *
   * As medidas vão em variáveis CSS, e só a regra do celular as lê: no
   * computador o painel continua sendo a caixa de 400px no canto.
   */
  const painelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const vv = window.visualViewport;
    const painel = painelRef.current;
    if (!vv || !painel) return;
    const ajustar = () => {
      painel.style.setProperty('--ac-alt', `${Math.round(vv.height)}px`);
      painel.style.setProperty('--ac-topo', `${Math.round(vv.offsetTop)}px`);
      // A última mensagem continua à vista quando a área encolhe. Sem conversa
      // não há última mensagem: o painel fica no topo, onde está a fala do Finn.
      if (painel.querySelector('.ac-voce, .ac-app')) fimRef.current?.scrollIntoView({ block: 'end' });
    };
    ajustar();
    vv.addEventListener('resize', ajustar);
    vv.addEventListener('scroll', ajustar);
    return () => {
      vv.removeEventListener('resize', ajustar);
      vv.removeEventListener('scroll', ajustar);
    };
  }, [aberto]);

  /** Só o que o servidor aceita: pergunta e intenção. Nunca valores. */
  const historico = () =>
    msgs.reduce<{ question: string; intent: string; month: string | null; categoryName: string | null }[]>(
      (acc, m, i) => {
        if (m.de !== 'voce' || !m.texto) return acc;
        const r = msgs[i + 1]?.resposta;
        acc.push({
          question: m.texto,
          intent: r?.intent ?? '',
          month: r?.month ?? null,
          categoryName: r?.categoryName ?? r?.budget?.categoryName ?? null,
        });
        return acc;
      }, []);

  useEffect(() => {
    /*
     * Só com conversa. Sem ela, rolar até o fim escondia o começo da pergunta
     * do Finn, que é alta: ela abria a bolha e via as opções sem a pergunta.
     */
    if (aberto && msgs.length > 0) fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs, aberto]);

  /*
   * Visto só quando a mensagem foi EXIBIDA — não quando a bolha abriu.
   *
   * Decisão de produto, e ela protege o caso que justifica a feature: um toque
   * errado no meio da rua não pode queimar o insight mais importante do ano,
   * sem desfazer. Este efeito depende do painel estar aberto E de haver
   * marcador, então ele só dispara depois do render que de fato mostrou o
   * texto.
   *
   * O estado local NÃO é limpo aqui. O marcador continua na tela desta sessão
   * — some do servidor, não debaixo dos olhos de quem está lendo.
   */
  useEffect(() => {
    if (!aberto || !marcador) return;
    marcarMarcadorVisto(marcador.id)
      .then(() => setPontoApagado(true))
      .catch(() => {/* já lido, ou sem rede */});
  }, [aberto, marcador]);

  /*
   * ── O ponto e o texto param de ser a mesma coisa ──────────────────────────
   *
   * Eram: `marcador` mandava nos dois, e não limpar o estado local mantinha os
   * dois. A razão escrita — "some do servidor, não debaixo dos olhos de quem
   * está lendo" — vale para o TEXTO dentro do painel, que não pode desaparecer
   * no meio da leitura.
   *
   * Não vale para o ponto no botão fechado. Depois de ler e fechar, ele
   * continuava dizendo "o Finn tem algo a dizer" sobre algo já lido, até a
   * próxima carga da página. O app afirmando o que não é mais verdade.
   *
   * Então o texto segue com `marcador`, e o ponto passa a olhar isto.
   *
   * ── Por que só depois da confirmação do servidor ─────────────────────────
   *
   * `.then()` e não antes da chamada: se a rede falhar, o servidor não sabe que
   * ela viu, e o marcador volta na próxima carga. Apagar o ponto aqui faria a
   * tela discordar do servidor — e a discordância reapareceria sozinha depois,
   * que é pior que o ponto ficar um pouco a mais.
   */
  const [pontoApagado, setPontoApagado] = useState(false);

  /*
   * Marcador novo acende o ponto de novo. `refreshTrigger` recarrega o
   * marcador, e sem isto o primeiro insight lido calaria todos os seguintes
   * pelo resto da sessão — um silêncio que ninguém pediu e que ninguém veria.
   */
  useEffect(() => { setPontoApagado(false); }, [marcador?.id]);

  // A carteira mudou lá fora: o fio ficou velho, porque as respostas dele foram
  // calculadas sobre outros dados.
  useEffect(() => { setMsgs([]); }, [refreshTrigger]);

  return (
    <>
      {!aberto && (
        <button className="ac-bolha" id="ac-ancora" onClick={() => setAberto(true)} aria-label="Abrir análise">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.8-.8L3 21l1.9-5A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Perguntar</span>
          {/*
            O ponto, e nada além de um ponto.

            Ele não interrompe: não pisca, não conta nada e não pede clique. É
            o oposto do push — a versão paciente do mesmo aviso, esperando ela
            abrir. `aria-label` porque um ponto colorido não existe para quem
            usa leitor de tela.
          */}
          {((marcador && !pontoApagado) || pergunta)
            && <i className="ac-ponto" aria-label="o Finn tem algo a dizer" />}
        </button>
      )}

      {quemEh && <QuemEhOFinn onFechar={() => setQuemEh(false)} />}

      {aberto && (
        <div className="ac" id="ac-ancora" role="dialog" aria-label="Análise das suas finanças" ref={painelRef}>
          <div className="ac-head">
            <span className="ac-titulo">Perguntar sobre as suas finanças</span>
            <div className="ac-acoes">
              {msgs.length > 0 && (
                <button className="ac-btn" onClick={() => setMsgs([])} title="Limpar conversa">limpar</button>
              )}
              {/*
                × e não −, e o rótulo diz "Fechar".
                
                O traço quer dizer "minimizar", e minimizar é uma ideia de
                janela: encolhe para uma barra e continua ali. No celular este
                painel é TELA CHEIA — não há barra nenhuma para onde encolher,
                e um traço no canto de uma tela cheia não parece saída.
                
                E mesmo no computador o traço prometia errado: o botão faz
                `setAberto(false)`, o painel some inteiro e volta a ser a
                bolha. Isso é fechar. A conversa fica guardada nos dois casos,
                então × não está mentindo sobre perder nada.
              */}
              <button className="ac-btn ac-min" onClick={() => setAberto(false)} aria-label="Fechar">×</button>
            </div>
          </div>

          <div className="ac-corpo">
            {/*
              A pergunta do Finn, no mesmo balão das falas dele. Fica aqui até
              ela responder ou dizer "é depois": fechar o painel não responde
              nada, e o ponto continua na bolha.
            */}
            {(pergunta || respondeu) && (
              <div className="ac-finn">
                <FinnAvatar
                  className="ac-finn-avatar"
                  tamanho={30}
                  onClick={() => setQuemEh(true)}
                />
                <div className="ac-finn-balao">
                  <span className="ac-finn-nome">Finn</span>
                  {pergunta ? (
                    <PerguntaDoFinn
                      portas={pergunta.portas}
                      nome={pergunta.nome}
                      onDeclarou={() => { setRespondeu(true); pergunta.onDeclarou(); }}
                      onDepois={pergunta.onDepois}
                    />
                  ) : (
                    <p className="ac-finn-texto">Guardado. É por aí que eu vou te acompanhar.</p>
                  )}
                </div>
              </div>
            )}

            {marcador && (
              <div className="ac-finn">
                <FinnAvatar
                  className="ac-finn-avatar"
                  tamanho={30}
                  onClick={() => setQuemEh(true)}
                />
                <div className="ac-finn-balao">
                  {/*
                    O nome é TEXTO, não parte do desenho.

                    O avatar é `aria-hidden`, então quem usa leitor de tela
                    descobre de quem é a fala por aqui — e quem enxerga também,
                    porque uma capivara de 30px sem nome do lado é um enfeite.
                  */}
                  <span className="ac-finn-nome">Finn</span>
                  <p className="ac-finn-texto">{comEnfase(marcador.texto)}</p>

                  {/*
                    O botão LEVA, não faz.

                    O painel do site orienta; o WhatsApp age. A regra é dela e
                    está guardada em `advisorSemInsight.test.ts`: "o painel foi
                    ABERTO por ela, e conselho não pedido dentro de um painel
                    aberto para outra coisa é ruído."

                    Uma versão deste componente teve um botão que criava a meta
                    ali mesmo. Foi desfeita: aqui ele fecha o chat e leva até a
                    tela onde criar meta sempre esteve.

                    Fechar o painel antes de navegar é o ponto — sem isso ela
                    chega na seção com o chat cobrindo metade dela.
                  */}
                  {marcador.destino && (
                    <button
                      className="ac-finn-ir"
                      onClick={() => {
                        setAberto(false);
                        /*
                         * O WhatsApp não é seção do quadro: ele abre um modal
                         * que vive no `AppContent`. Os outros destinos são
                         * `sec-*` e viajam pelo hash, que é o link profundo
                         * que o app já usa.
                         */
                        if (marcador.destino === 'whatsapp') onAbrirWhatsapp?.();
                        else if (marcador.destino === 'conector') onAbrirConector?.();
                        else if (marcador.destino === 'coach') window.location.hash = 'sec-goals';
                        else if (marcador.destino === 'categorias') {
                          /*
                           * As categorias se gerenciam num painel DENTRO do card
                           * do gráfico. O hash leva até o card; o evento abre o
                           * painel — sem ele ela chegaria num gráfico, sem saber
                           * que a resposta mora atrás da engrenagem.
                           */
                          window.location.hash = 'sec-chart';
                          window.dispatchEvent(new Event(EVENTO_ABRIR_CATEGORIAS));
                        }
                        else window.location.hash = marcador.destino!;
                      }}
                    >
                      {ROTULO_DO_DESTINO[marcador.destino] ?? 'Ver'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {msgs.length === 0 && !marcador && !pergunta && !respondeu && (
              <div className="ac-vazio">
                <p>Pergunte o que quiser sobre as suas finanças. Depois dá para aprofundar: <em>“e em julho?”</em>, <em>“e se fosse 2000?”</em>.</p>
                <p className="ac-vazio-nota">Todo número da resposta é calculado, nunca escrito pelo modelo.</p>
              </div>
            )}

            {msgs.map((m, i) =>
              m.de === 'voce' ? (
                <div className="ac-voce" key={i}>{m.texto}</div>
              ) : (
                <div className="ac-app" key={i}>
                  {m.resposta && <Resposta a={m.resposta} />}
                  {m.resposta?.propostaDeCoach && <PropostaDoCoach p={m.resposta.propostaDeCoach} />}
                  {m.resposta?.propostaDeAviso && <PropostaDoAviso p={m.resposta.propostaDeAviso} />}
                </div>
              ),
            )}
            <div ref={fimRef} />
          </div>

          <div className="ac-pe">
            <AskField<AdvisorAnswer>
              placeholder="pergunte qualquer coisa…"
              ariaLabel="Perguntar sobre as suas finanças"
              multilinha
              exemplos={msgs.length === 0 ? exemplosDoChat : []}
              textoInicial={abrirCom}
              onAsk={async texto => {
                setMsgs(m => [...m, { de: 'voce', texto }]);
                const r = await askAdvisor(texto, historico());
                if (!r.answer) {
                  /*
                   * O app dizendo que não sabe é o melhor momento para falar do
                   * conector — e só aí. Quem decide se cabe é o servidor, em
                   * código: perguntas de mercado, empréstimo e produto
                   * financeiro são justamente as que o prompt daqui proíbe, e
                   * as que o Claude e o ChatGPT respondem com os números dela
                   * na mão.
                   */
                  throw new Error([
                    r.problem ?? 'Não entendi a pergunta.',
                    r.sugestao === 'conector'
                      ? 'Esse tipo de pergunta o Claude e o ChatGPT respondem melhor: conecte no menu do seu perfil, em "Conectar ao Claude / ChatGPT".'
                      : null,
                  ].filter(Boolean).join(' '));
                }
                return r.answer;
              }}
              onResult={resposta => setMsgs(m => [...m, { de: 'app', resposta }])}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AdvisorChat;
